import { CSSProperties, useRef, useState } from 'react';

import { Trigger as GalleryDialogTrigger, Trigger } from '@radix-ui/react-dialog';
import clsx from 'clsx';
import dynamic, { LoaderComponent } from 'next/dynamic';
import { SwiperProps } from 'swiper/swiper-react';

import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron-right.svg';
import { IImageMedia } from '~/lib/data-contract';
import { CarouselProps } from '~/shared/components/Carousel/Carousel';
import { GalleryDialog } from '~/shared/components/GalleryDialog/GalleryDialog';
import { GalleryDialogContent } from '~/shared/components/GalleryDialog/GalleryDialogContent';
import { Icon } from '~/shared/components/Icon/Icon';
import { lgMinWidth } from '~/theme/lib/breakpoints';

const Carousel = dynamic(() => import('~/shared/components/Carousel/Carousel').then((module) => module.Carousel) as LoaderComponent<CarouselProps>, { ssr: false });

import { ProductDetailsCarouselItem } from './../ProductDetailsCarouselItem/ProductDetailsCarouselItem';
import styles from './productDetailsCarousel.module.css';

export type ProductDetailsCarouselProps = {
	images: IImageMedia[];
	opacity: number;
	imageAltText?: string;
};

export const ProductDetailsCarousel = ({ images, opacity, imageAltText }: ProductDetailsCarouselProps) => {
	const prevRef = useRef<HTMLButtonElement>(null);
	const nextRef = useRef<HTMLButtonElement>(null);
	const [initialSlide, setInitialSlideIndex] = useState<number>(0);
	const initialWindowWidth = useRef(typeof window !== 'undefined' ? window.innerWidth : 375);
	const carouselOptions: SwiperProps = {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		slidesPerView: 1,
		centeredSlides: true,
		spaceBetween: 40,
		loop: true,
		loopPreventsSlide: false,
		freeMode: false,
		pagination: {
			clickable: true,
			type: 'bullets',
		},
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		speed: 800,
		grabCursor: true,
		touchRatio: initialWindowWidth.current >= lgMinWidth ? 3 : 1,
	};
	const hasMultipleImages = images.length > 1;

	const handleSlideClick = (index: number) => {
		setInitialSlideIndex(index);
	};

	return (
		<GalleryDialog>
			<div
				className={styles.root}
				style={{ '--pdp-carousel-opacity': opacity } as CSSProperties}
			>
				<button
					className={clsx(styles.navPrevious, { [styles.isVisible]: hasMultipleImages })}
					ref={prevRef}
					aria-label="Previous image"
				>
					<Icon
						size="2xl"
						aria-hidden
						children={<ChevronLeft />}
					/>
				</button>
				{hasMultipleImages ? (
					<Carousel
						prevRef={prevRef}
						nextRef={nextRef}
						optionsOverride={carouselOptions}
						items={images.map((image, index) => (
							<GalleryDialogTrigger
								key={image.src}
								onClick={() => handleSlideClick(index)}
								asChild
							>
								<ProductDetailsCarouselItem
									priority={index === 0}
									image={{ ...image, alt: imageAltText }}
								/>
							</GalleryDialogTrigger>
						))}
					/>
				) : (
					<Trigger
						className={styles.singleImageTrigger}
						onClick={() => handleSlideClick(0)}
						asChild
					>
						<ProductDetailsCarouselItem
							priority
							image={{ ...images[0], alt: imageAltText }}
						/>
					</Trigger>
				)}
				<button
					className={clsx(styles.navNext, { [styles.isVisible]: hasMultipleImages })}
					ref={nextRef}
					aria-label="Next image"
				>
					<Icon
						size="2xl"
						aria-hidden
						children={<ChevronRight />}
					/>
				</button>
			</div>
			<GalleryDialogContent
				initialSlide={initialSlide}
				mediaItems={images}
			></GalleryDialogContent>
		</GalleryDialog>
	);
};
